import { useState } from 'react'
import { ChartFrame, ChartTooltip, type TooltipState } from './Chart'
import { exactNumber, seriesColor } from './scale'

const SIZE = 240
const RADIUS = 92
const THICKNESS = 30
/** The surface gap, expressed as the angle it subtends at this radius. */
const GAP_DEGREES = 1.6

export interface Slice {
  label: string
  value: number
}

/**
 * How a whole divides — the mix of severities, the split of a fleet by state.
 *
 * A donut and not a pie because the hole is where the total goes, and the total is the number
 * somebody reads first. It is used only where the parts genuinely sum to a meaningful whole and there
 * are few of them; beyond about five slices the angles stop being comparable and a bar chart is the
 * honest form. Callers fold the tail into one "other" slice rather than drawing nine.
 */
export function Donut({
  title,
  description,
  slices,
  totalLabel
}: {
  title: string
  description: string
  slices: Slice[]
  totalLabel: string
}) {
  const [hover, setHover] = useState<TooltipState | null>(null)

  const total = slices.reduce((sum, slice) => sum + slice.value, 0)
  const center = SIZE / 2
  let cursor = -90

  return (
    <ChartFrame
      title={title}
      description={description}
      categories={slices.map((slice) => slice.label)}
      series={[{ key: 'count', label: 'Count', values: slices.map((slice) => slice.value) }]}
      categoryHeading="Group"
      hideLegend
    >
      <ul className="chart-legend">
        {slices.map((slice, index) => (
          <li key={slice.label}>
            <span className="chart-swatch" style={{ background: seriesColor(index) }} aria-hidden="true" />
            {slice.label}
          </li>
        ))}
      </ul>

      <div className="chart-surface chart-surface-narrow">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          preserveAspectRatio="xMidYMid meet"
          className="chart-svg chart-svg-square"
          onPointerLeave={() => setHover(null)}
        >
          {total > 0 &&
            slices.map((slice, index) => {
              if (slice.value <= 0) return null

              const sweep = (slice.value / total) * 360
              const start = cursor
              cursor += sweep

              // A single slice covering the whole ring has no neighbour to be separated from, and
              // trimming it for a gap would leave a notch in an otherwise complete circle.
              const gap = slices.filter((candidate) => candidate.value > 0).length > 1 ? GAP_DEGREES : 0
              const end = start + Math.max(0.4, sweep - gap)

              return (
                <path
                  key={slice.label}
                  d={arc(center, RADIUS, THICKNESS, start, end)}
                  fill={seriesColor(index)}
                  onPointerEnter={() =>
                    setHover({
                      x: 0.5,
                      y: 0.5,
                      heading: slice.label,
                      rows: [{ label: 'Devices', value: slice.value, color: seriesColor(index) }]
                    })
                  }
                />
              )
            })}

          <text x={center} y={center - 4} textAnchor="middle" className="chart-hero">
            {exactNumber(total)}
          </text>
          <text x={center} y={center + 18} textAnchor="middle" className="chart-tick">
            {totalLabel}
          </text>
        </svg>
        <ChartTooltip state={hover} />
      </div>
    </ChartFrame>
  )
}

/** One ring segment, as a filled path between an outer and an inner arc. */
function arc(center: number, radius: number, thickness: number, startDegrees: number, endDegrees: number): string {
  const inner = radius - thickness
  const start = (startDegrees * Math.PI) / 180
  const end = (endDegrees * Math.PI) / 180
  const large = endDegrees - startDegrees > 180 ? 1 : 0

  const point = (r: number, angle: number) =>
    `${center + r * Math.cos(angle)},${center + r * Math.sin(angle)}`

  return [
    `M ${point(radius, start)}`,
    `A ${radius} ${radius} 0 ${large} 1 ${point(radius, end)}`,
    `L ${point(inner, end)}`,
    `A ${inner} ${inner} 0 ${large} 0 ${point(inner, start)}`,
    'Z'
  ].join(' ')
}
